import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { Banner, Button, Card, Field, Input } from "@magarac/ui";
import type { HoursRange } from "@magarac/shared/schemas/common";
import { addDays, localToUtc } from "@magarac/shared/time/tz";
import { resourceDetailSchema, timeOffSchema } from "@magarac/shared/schemas/resource";
import { HelpIcon } from "../../../components/HelpIcon";
import { WeeklyHoursEditor } from "../../../components/WeeklyHoursEditor";
import { api } from "../../../lib/api";
import { messageFor } from "../../../lib/errors";
import { useSetting } from "../../../lib/settings";
import { useTenant } from "../../../lib/tenant";
import { CalendarConnectCard } from "../../../components/CalendarConnectCard";
import { CalendarFeedCard } from "../../../components/CalendarFeedCard";

export const Route = createFileRoute("/_app/schedule/$id")({
  component: ResourceDetailPage,
});

/** SCH-2 per-person hours and SCH-13 time off. Screen keys: schedule.hours, schedule.timeOff */
function ResourceDetailPage() {
  const { id } = Route.useParams();
  const { active } = useTenant();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ["resources", active?.id, id],
    queryFn: () => api.get(`/resources/${id}`, resourceDetailSchema),
    enabled: Boolean(active),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["resources", active?.id, id] });
  const [error, setError] = useState<string | null>(null);

  const saveHours = useMutation({
    mutationFn: (weekly: HoursRange[]) =>
      api.put(`/resources/${id}/hours`, { weekly }, z.object({ weekly: z.array(z.any()) })),
    onSuccess: refresh,
    onError: (err) => setError(messageFor(err)),
  });
  const [offFrom, setOffFrom] = useState("");
  const [offTo, setOffTo] = useState("");
  const [offReason, setOffReason] = useState("");
  const [offNotice, setOffNotice] = useState<string | null>(null);
  const tz = useSetting("scheduling").value?.timezone ?? "UTC";
  const addTimeOff = useMutation({
    mutationFn: () =>
      api.post(
        `/resources/${id}/time-off`,
        {
          // Whole days in the business's time zone, not the device's (an owner away from home
          // would otherwise block the wrong hours).
          startAt: new Date(localToUtc(offFrom, 0, tz)).toISOString(),
          endAt: new Date(localToUtc(addDays(offTo || offFrom, 1), 0, tz) - 1000).toISOString(),
          allDay: true,
          reason: offReason || undefined,
        },
        z.object({ timeOff: timeOffSchema }),
      ),
    onSuccess: async (res) => {
      setOffFrom("");
      setOffTo("");
      setOffReason("");
      const n = res.timeOff.overlappingVisits ?? 0;
      const who = detail.data?.resource.name ?? "them";
      setOffNotice(
        n > 0
          ? `Time off added. ${n} visit${n === 1 ? " is" : "s are"} still booked for ${who} during it: move ${n === 1 ? "it" : "them"} on the calendar.`
          : null,
      );
      await refresh();
    },
    onError: (err) => setError(messageFor(err)),
  });
  const removeTimeOff = useMutation({
    mutationFn: (timeOffId: string) =>
      api.delete(`/resources/${id}/time-off/${timeOffId}`, z.object({ ok: z.literal(true) })),
    onSuccess: refresh,
  });

  if (detail.error) return <Banner tone="danger">{messageFor(detail.error)}</Banner>;
  const d = detail.data;
  if (!d) return <p className="text-muted">Loading…</p>;

  return (
    <div className="mx-auto max-w-3xl">
      <Link to="/schedule" className="text-sm text-brand-600 underline">
        ← Everyone on the schedule
      </Link>
      <div className="mb-6 mt-1 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{d.resource.name}</h1>
        <HelpIcon screen="schedule.hours" />
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <HoursCard
        key={`${id}-${detail.dataUpdatedAt}`}
        initial={d.weeklyHours}
        onSave={(w) => saveHours.mutateAsync(w)}
        busy={saveHours.isPending}
        saved={saveHours.isSuccess}
      />
      <CalendarConnectCard resourceId={id} back={`/schedule/${id}`} />
      <CalendarFeedCard resourceId={id} />
      <Card title="Time off" className="mt-6">
        <div className="grid gap-2 md:grid-cols-[1fr_1fr_2fr_auto] md:items-end">
          <Field id="off-from" label="First day">
            <Input
              id="off-from"
              type="date"
              value={offFrom}
              onChange={(e) => setOffFrom(e.target.value)}
            />
          </Field>
          <Field id="off-to" label="Last day" hint="Leave empty for one day.">
            <Input
              id="off-to"
              type="date"
              value={offTo}
              onChange={(e) => setOffTo(e.target.value)}
            />
          </Field>
          <Field id="off-reason" label="Reason (optional)">
            <Input
              id="off-reason"
              value={offReason}
              onChange={(e) => setOffReason(e.target.value)}
            />
          </Field>
          <div className="mb-4">
            {offNotice ? <Banner tone="warn">{offNotice}</Banner> : null}
            <Button
              onClick={() => {
                if (offTo && offTo < offFrom)
                  return setError("The last day is before the first day.");
                addTimeOff.mutate();
              }}
              busy={addTimeOff.isPending}
              disabled={!offFrom}
            >
              Add time off
            </Button>
          </div>
        </div>
        {d.timeOff.length === 0 ? (
          <p className="text-sm text-muted">No time off scheduled.</p>
        ) : (
          <ul>
            {d.timeOff.map((t) => (
              <li
                key={t.id}
                className="flex items-center justify-between border-b border-line py-2 last:border-0"
              >
                <span>
                  {new Date(t.startAt).toLocaleDateString()} to{" "}
                  {new Date(t.endAt).toLocaleDateString()}
                  {t.reason ? <span className="text-muted"> · {t.reason}</span> : null}
                </span>
                <Button variant="ghost" onClick={() => removeTimeOff.mutate(t.id)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function HoursCard({
  initial,
  onSave,
  busy,
  saved,
}: {
  initial: HoursRange[];
  onSave: (w: HoursRange[]) => Promise<unknown>;
  busy: boolean;
  saved: boolean;
}) {
  const [weekly, setWeekly] = useState<HoursRange[]>(initial);
  const invalid = weekly.some((r) => r.endMin <= r.startMin);
  return (
    <Card title="Working hours">
      <p className="mb-4 text-sm text-muted">
        Only the part of these hours inside the business hours can be booked.
      </p>
      <WeeklyHoursEditor value={weekly} onChange={setWeekly} />
      {invalid ? (
        <p className="mt-2 text-sm text-danger">End time must be after start time.</p>
      ) : null}
      <div className="mt-4 flex items-center gap-3">
        <Button onClick={() => void onSave(weekly)} busy={busy} disabled={invalid}>
          Save hours
        </Button>
        {saved ? <span className="text-sm text-ok">Saved.</span> : null}
      </div>
    </Card>
  );
}
